import { Box, Flex, Grid, Heading, Input, Textarea, useDisclosure } from '@chakra-ui/react';
import { Image } from '@components/AssetWrapper';
import { APIKongVX, KollectionType } from '@cyberkongz/api-interfaces';
import { Kong } from '@cyberkongz/database';
import {
  Card,
  EditIcon,
  ErrorAlert,
  Field,
  FormButtonsEditing,
  FormButtonsWithApproval,
  FormCompleted,
  FormStepDescription,
  FormStepsBar,
  FormSummaryIcon,
  FormWithTxStage,
  getKongImageByType,
  KongActionIcon,
  Modal,
  TabsContainer,
  TabsItem,
  TokenAllowanceAlert,
  WithExpectedNetworkConnectionLegacy,
} from '@cyberkongz/ui';
import {
  amountToBigNumber,
  formatCryptoBalance,
  getKongzContract,
  getKongzVXContract,
  useBananaAllowance,
  useBananaApprove,
  useBananaBalance,
  useChainEnhanced,
  useWriteContractWithTracking,
} from '@cyberkongz/ui-blockchain';
import { useSharedUI } from '@features/sharedUI/SharedUIProvider';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Hash } from 'viem';
import { useSimulateContract } from 'wagmi';

type EditKongTabType = 'name' | 'bio';

const TABS: { label: string; id: EditKongTabType }[] = [
  {
    label: 'NAME',
    id: 'name',
  },
  {
    label: 'BIO',
    id: 'bio',
  },
];

function FormSummary({ id, type, label }: { id: number; type: KollectionType; label: string }) {
  return (
    <Card>
      <Flex textStyle="p-sm" align="center" justify="space-between">
        <Grid gap={1} textStyle="p-sm" textAlign="center" fontWeight="medium">
          <Image
            borderRadius="sm"
            src={getKongImageByType(id, type)}
            css={{ w: 24, h: type === 'vx' ? 'auto' : 24 }}
            alt={`Kong ${id} preview`}
          />
          {type === 'genesis' ? 'Genesis' : type === 'baby' ? 'Baby' : 'VX'} {id}
        </Grid>
        <FormSummaryIcon />
        <Box>{label}</Box>
      </Flex>
    </Card>
  );
}

function BananaCost({ bananaCost }: { bananaCost: bigint }) {
  return (
    <Flex mt={2} fontSize="sm" color="textTertiary" align="center" gap={1}>
      Cost: {formatCryptoBalance(bananaCost)} $BANANA
      <Image src="/static/icons/tokens/banana.png" alt="Banana token" />
    </Flex>
  );
}

export interface EditActionFormViewProps {
  activeTab: EditKongTabType;
  stage: FormWithTxStage;
  name: string;
  bio: string;
  id: number;
  type: KollectionType;
  txHash?: string;
  setStage: Dispatch<SetStateAction<FormWithTxStage>>;
  setName: Dispatch<SetStateAction<string>>;
  setBio: Dispatch<SetStateAction<string>>;
  setActiveTab: Dispatch<SetStateAction<EditKongTabType>>;
  writeContract?: () => void;
  hasEnoughBanana: boolean;
  hasEnoughAllowance: boolean;
  bananaCost: bigint;
  approve?: () => void;
  dataApprove?: Hash;
  reset: () => void;
}

export function EditActionFormView({
  activeTab,
  stage,
  name,
  bio,
  id,
  type,
  txHash,
  setStage,
  setName,
  setBio,
  setActiveTab,
  writeContract,
  hasEnoughBanana,
  hasEnoughAllowance,
  bananaCost,
  approve,
  dataApprove,
  reset,
}: EditActionFormViewProps) {
  const { open: isOpen, onClose, onOpen } = useDisclosure();
  const { kongVXTheme } = useSharedUI();

  const isVX = type === 'vx';
  const isEditingName = activeTab === 'name';

  function closeModal() {
    onClose();
    reset();
  }

  function switchTab(tab: EditKongTabType) {
    setActiveTab(tab);
    reset();
  }

  return (
    <>
      <KongActionIcon label="Edit" onClick={onOpen}>
        <EditIcon color={kongVXTheme?.text} />
      </KongActionIcon>
      <Modal
        variant="form"
        {...{
          isOpen,
          onClose: closeModal,
          headerComponent: stage !== 'completed' ? <Heading size="xs">Edit Kong</Heading> : null,
        }}
      >
        <WithExpectedNetworkConnectionLegacy expectedNetwork="mainnet">
          <Grid gap={8}>
            {stage !== 'completed' ? (
              <>
                {!isVX ? (
                  <TabsContainer css={{ px: 6, mx: -6 }}>
                    {TABS.map(({ id, label }) => (
                      <TabsItem
                        key={id}
                        {...{
                          activeTab,
                          setActiveTab: switchTab,
                          tab: id,
                          noShadow: true,
                        }}
                      >
                        {label}
                      </TabsItem>
                    ))}
                  </TabsContainer>
                ) : null}
                <FormStepsBar totalSteps={2} currentStep={stage === 'editing' ? 1 : 2} />
                {stage === 'editing' ? (
                  <Grid gap={6}>
                    <FormStepDescription
                      title={`1. Kong ${isEditingName ? 'Name' : 'Bio'}`}
                      description={`Please enter ${isEditingName ? 'Name' : 'Bio'} for your Kong`}
                    />
                    <Field label={isEditingName ? 'Name:' : 'Bio:'}>
                      {isEditingName ? (
                        <Input value={name} onChange={(e) => setName(e.target.value)} />
                      ) : (
                        <Textarea value={bio} onChange={(e) => setBio(e.target.value)} />
                      )}
                      <BananaCost bananaCost={bananaCost} />
                    </Field>
                    {!hasEnoughBanana ? (
                      <ErrorAlert>You don`t have enough $BANANA</ErrorAlert>
                    ) : null}
                    <FormButtonsEditing
                      onClickCancel={closeModal}
                      setStage={setStage}
                      isDisabledNext={isEditingName ? name === '' : bio === ''}
                    />
                  </Grid>
                ) : (
                  <Grid gap={6}>
                    <FormStepDescription title={`2. Confirm ${isEditingName ? 'Name' : 'Bio'}`} />
                    <FormSummary {...{ id, type, label: isEditingName ? name : bio }} />
                    <BananaCost bananaCost={bananaCost} />
                    <TokenAllowanceAlert
                      hasEnoughAllowance={hasEnoughAllowance}
                      txHash={dataApprove}
                      token="$BANANA"
                    />
                    <FormButtonsWithApproval
                      stage={stage}
                      setStage={setStage}
                      confirm={writeContract}
                      hasEnoughAllowance={hasEnoughAllowance}
                      approve={approve}
                      txHash={dataApprove}
                    />
                  </Grid>
                )}
              </>
            ) : (
              <FormCompleted
                {...{
                  onClose: closeModal,
                  txHash,
                  title: `${isEditingName ? 'Name' : 'Bio'} change completed`,
                  summary: <FormSummary {...{ id, type, label: isEditingName ? name : bio }} />,
                }}
              />
            )}
          </Grid>
        </WithExpectedNetworkConnectionLegacy>
      </Modal>
    </>
  );
}

const BANANA_COSTS = {
  // only on mainnet
  bio: 100,
  // only on mainnet
  name: 10,
  // only on mainnet
  name_vx: 2,
  // only on mainnet, legacy
  // breed: 600,
};

function getBananaCost(type: KollectionType, isEditingName: boolean) {
  if (type === 'vx') {
    return BANANA_COSTS.name_vx;
  }

  if (isEditingName) {
    return BANANA_COSTS.name;
  }

  return BANANA_COSTS.bio;
}

export function EditActionForm({ kong, type }: { kong: Kong | APIKongVX; type: KollectionType }) {
  const { id } = kong;
  const isVX = type === 'vx';

  const [stage, setStage] = useState<FormWithTxStage>('editing');
  const [activeTab, setActiveTab] = useState<EditKongTabType>('name');
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const { bananaBalance } = useBananaBalance();
  const { chain } = useChainEnhanced();
  const { bananaAllowance: allowanceVX } = useBananaAllowance({
    spender: getKongzVXContract(chain).address,
  });

  const isEditingName = activeTab === 'name';
  const isValidNameBio = useMemo(() => {
    if (isEditingName) {
      return name === '' ? false : true;
    }

    return bio === '' ? false : true;
  }, [name, bio, isEditingName]);

  const bananaCost = amountToBigNumber(getBananaCost(type, isEditingName));
  const hasEnoughBanana = bananaBalance ? bananaBalance > bananaCost : false;
  const hasEnoughAllowance = isVX ? (allowanceVX ? allowanceVX > bananaCost : false) : true;

  const { data: dataApprove, write: approve } = useBananaApprove({
    spender: getKongzVXContract(chain).address,
    enabled: !hasEnoughAllowance,
  });

  const { data: dataSimulateKongz } = useSimulateContract({
    ...getKongzContract(chain),
    functionName: isEditingName ? 'changeName' : 'changeBio',
    args: [BigInt(id), isEditingName ? name : bio],
    query: {
      enabled:
        !isVX &&
        chain?.type === 'mainnet' &&
        isValidNameBio &&
        hasEnoughBanana &&
        hasEnoughAllowance,
    },
  });

  const { data: dataSimulateKongzVX } = useSimulateContract({
    ...getKongzVXContract(chain),
    functionName: 'changeName',
    args: [BigInt(id), name],
    query: {
      enabled:
        isVX &&
        chain?.type === 'mainnet' &&
        isValidNameBio &&
        hasEnoughBanana &&
        hasEnoughAllowance,
    },
  });

  const { txHash, writeContract } = useWriteContractWithTracking({
    dataSimulate: isVX ? dataSimulateKongzVX : dataSimulateKongz,
    onInProgress: () => setStage('inProgress'),
    onCompleted: () => setStage('completed'),
  });

  function reset() {
    setStage('editing');
    setName('');
    setBio('');
  }

  return (
    <EditActionFormView
      {...{
        stage,
        setStage,
        id,
        type,
        writeContract,
        txHash,
        name,
        setName,
        bio,
        setBio,
        activeTab,
        setActiveTab,
        hasEnoughBanana,
        hasEnoughAllowance,
        bananaCost,
        approve,
        dataApprove,
        reset,
      }}
    />
  );
}
